import {uuid4} from '@sentry/utils'
import {UIEventEmitter} from '../../../../shared/scripts/UIEventEmitter'
import {DiscordCommand, DiscordCustomEvent, DiscordContentFrameMessageData, DiscordServerProfile, DiscordUserPermissions, DiscordEvent} from '../../../../../shared/DiscordActivityTypes'
import {DiscordPopupFromMountHandler} from './DiscordPopupFromMountHandler'

interface PromiseCallbacks {
    resolve: (value: unknown) => unknown
    reject: (reason: unknown) => unknown
}

export class DiscordContentFrameCommunicationHandler {
    voiceChannelId: string
    inDiscordServer: boolean
    userDiscordAccessToken: string
    serverProfile: DiscordServerProfile
    userPermissions: DiscordUserPermissions
    referrerId: string | undefined
    referralTypeId: string | undefined
    isInitialLoad: boolean

    private readonly readyPromise: Promise<void>
    private readonly messagesWaitingForResponse: Map<string, PromiseCallbacks> = new Map()

    readonly subscriptionListener = new UIEventEmitter()
    private readonly mountPopupHandler: DiscordPopupFromMountHandler

    constructor() {
        window.addEventListener('message', (data) => this.handleMessageReceivedFromMount(data))
        this.mountPopupHandler = new DiscordPopupFromMountHandler(this)

        this.readyPromise = new Promise<void>((resolve) => {
            this.subscriptionListener.subscribeOnce(DiscordCustomEvent.Ready, ({instanceId, serverId, userDiscordAccessToken, serverProfile, userPermissions, referrerId, referralTypeId, isInitialLoad}) => {
                this.voiceChannelId = instanceId
                this.inDiscordServer = serverId != null
                this.userDiscordAccessToken = userDiscordAccessToken
                this.serverProfile = serverProfile
                this.userPermissions = userPermissions
                this.referrerId = referrerId
                this.referralTypeId = referralTypeId
                this.isInitialLoad = isInitialLoad
                resolve()
            })
        })

        void this.sendMessageToMount({command: DiscordCommand.ContentFrameReady})
    }

    async waitToBeReady() {
        return this.readyPromise
    }

    async subscribeToDiscordEvent(event: DiscordEvent, callback: (data: any) => any) {
        const listenerCount = this.subscriptionListener.listenerCount(event)

        const firstTimeSubscribing = listenerCount == 0
        if(firstTimeSubscribing) {
            await this.sendMessageToMount({
                command: DiscordCommand.Subscribe,
                subscription: event,
            })
        }

        const listener = this.subscriptionListener.subscribe(event, callback)
        return listener
    }

    async unsubscribeFromDiscordEvent(event: DiscordEvent, callback: (data: any) => any) {
        const listenerCount = this.subscriptionListener.listenerCount(event)

        const onlyOneListenerLeft = listenerCount == 1
        if(onlyOneListenerLeft) {
            await this.sendMessageToMount({
                command: DiscordCommand.Unsubscribe,
                event: event,
            })
        }

        this.subscriptionListener.unsubscribe(event, callback)
    }

    handleMessageReceivedFromMount({data: messageData, origin}: MessageEvent<DiscordContentFrameMessageData>) {
        if(typeof messageData !== 'object' || Array.isArray(messageData) || messageData === null) return

        try {
            const referrerOrigin = new URL(document.referrer).origin
            if(origin !== referrerOrigin) return
        } catch(e) {
            return
        }

        const {messageId, event, command, data, args} = messageData

        if(event === DiscordCustomEvent.CreateMountPopup) {
            this.mountPopupHandler.createPopup(data)
            return
        }

        if(command === DiscordCommand.DispatchDiscordEvent) {
            if(event == null) throw new Error('Event is missing')
            this.subscriptionListener.emit(event, data)
        }

        if(messageId == undefined) return

        // Resolve the promise that was waiting for a response from the mount
        this.messagesWaitingForResponse.get(messageId)?.resolve(data)
        this.messagesWaitingForResponse.delete(messageId)
    }

    sendMessageToMount(messageData: DiscordContentFrameMessageData): Promise<any> {
        // eslint-disable-next-line no-console
        console.log('Sending message', messageData)
        const messageId = uuid4()
        window.parent.postMessage({
            messageId: messageId,
            ...messageData,
        }, document.referrer ? document.referrer : '*')

        const waitUntilMountRespondsPromise = new Promise((resolve, reject) => {
            this.messagesWaitingForResponse.set(messageId, {resolve: resolve, reject: reject})
        })

        return waitUntilMountRespondsPromise
    }
}
